/* Super Admin > Email Templates — manage the "Executive Clarity" master email
   design: edit role-aware welcome copy + brand settings, live-preview
   (desktop / mobile / plain-text), and send a real test email.
   Mounted at /app/email-templates. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { withTimeout } from "@/hooks/useLoadState";
import { toast } from "sonner";
import { Loader2, Send, Save, Monitor, Smartphone, FileText, Palette, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, children }) => (
  <div className="mb-4">
    <Label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const EmailTemplatesAdminPage = ({ session, business }) => {
  const [data, setData] = useState(null);
  const [activeKey, setActiveKey] = useState("welcome_lead");

  // Stage 8 (3.5): when filtered by business, default the selection to a
  // template that belongs to this business (keeps shared generic/brand usable).
  useEffect(() => {
    if (!business || !data?.templates) return;
    const set = business === "myschoolrx" ? ["Portfolio Manager", "School User"] : ["Lead Manager"];
    const allowed = data.templates.filter((t) => set.includes(t.role_label)).map((t) => t.key);
    if (!["generic", "brand", ...allowed].includes(activeKey) && allowed.length) {
      setActiveKey(allowed[0]);
    }
  }, [business, data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState(null);
  const [brandForm, setBrandForm] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [preview, setPreview] = useState({ html: "", text: "" });
  const [mode, setMode] = useState("desktop");
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const debRef = useRef(null);

  const headers = useMemo(() => ({
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
    "x-tenant-id": session?.tenant?.id || "tenant_1",
  }), [session]);

  const isBrand = activeKey === "brand";
  const isGeneric = activeKey === "generic";

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const r = await withTimeout(axios.get(`${API}/api/admin/email-templates`, { headers }));
      setData(r.data);
      setBrandForm({ ...r.data.brand });
    } catch (e) { setLoadErr(e?.response?.data?.detail || e?.message || "Could not load templates"); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  // load selected template into form
  useEffect(() => {
    if (!data) return;
    if (isBrand || isGeneric) { setForm(null); return; }
    const t = data.templates.find((x) => x.key === activeKey);
    if (t) setForm({ ...t.config, bullets: (t.config.bullets || []).join("\n") });
  }, [activeKey, data, isBrand, isGeneric]);

  const buildOverrides = useCallback(() => {
    if (!form) return {};
    return { ...form, bullets: (form.bullets || "").split("\n").map((s) => s.trim()).filter(Boolean) };
  }, [form]);

  const runPreview = useCallback(async () => {
    try {
      const body = isBrand || isGeneric
        ? { key: isBrand ? "generic" : "generic", brand_overrides: brandForm || {} }
        : { key: activeKey, overrides: buildOverrides(), brand_overrides: brandForm || {} };
      const r = await axios.post(`${API}/api/admin/email-templates/preview`, body, { headers });
      setPreview({ html: r.data.html, text: r.data.text });
    } catch (e) { /* silent preview errors */ }
  }, [activeKey, isBrand, isGeneric, brandForm, buildOverrides, headers]);

  // debounced live preview on any change
  useEffect(() => {
    if (!data) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(runPreview, 350);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [form, brandForm, activeKey, data, runPreview]);

  const saveTemplate = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/admin/email-templates/${activeKey}`, buildOverrides(), { headers });
      toast.success("Template saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const saveBrand = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/admin/email-templates/brand`, brandForm || {}, { headers });
      toast.success("Brand settings saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const body = { key: isBrand || isGeneric ? "generic" : activeKey, to: testTo,
        overrides: isBrand || isGeneric ? undefined : buildOverrides(), brand_overrides: brandForm || {} };
      const r = await axios.post(`${API}/api/admin/email-templates/test-send`, body, { headers });
      if (r.data.status === "sent") toast.success(`Test email sent to ${testTo}`);
      else if (r.data.status === "failed") toast.error(`Delivery failed: ${r.data.delivery_error || "unknown"}`);
      else toast.info("Logged (no delivery — check Resend config)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    setBusy(false);
  };

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upB = (k, v) => setBrandForm((f) => ({ ...f, [k]: v }));

  if (!data) return loadErr
    ? <div role="alert" className="p-6" data-testid="email-templates-error"><div className="text-[15px] font-semibold text-[#991b1b]">Failed to load email templates</div><div className="text-[13px] text-[#4b5563] mt-1">{loadErr}</div><button onClick={load} className="mt-3 h-9 px-4 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-slate-900 hover:bg-slate-50" data-testid="email-templates-retry">Retry</button></div>
    : <div className="text-slate-500 text-sm p-6" aria-busy="true"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading email templates…</div>;

  const navItems = [
    ...data.templates
      .filter((t) => {
        if (!business) return true;
        const set = business === "myschoolrx"
          ? ["Portfolio Manager", "School User"]
          : ["Lead Manager"];
        return set.includes(t.role_label);
      })
      .map((t) => ({ key: t.key, label: t.role_label, sub: "Welcome email", icon: Mail, color: t.accent })),
    { key: "generic", label: "Standard Notification", sub: "Resets · links · alerts", icon: FileText, color: "#1d4ed8" },
    { key: "brand", label: "Brand Settings", sub: "Header · hero · footer", icon: Palette, color: "#16233f" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="email-templates-admin-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Email Templates</h1>
        <p className="text-sm text-slate-500 mt-1">Manage the Executive Clarity master email design. Edit copy, preview across devices, and send a test.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,520px)] gap-5">
        {/* Nav */}
        <div className="space-y-1.5">
          {navItems.map((n) => {
            const Icon = n.icon; const active = activeKey === n.key;
            return (
              <button key={n.key} onClick={() => setActiveKey(n.key)} data-testid={`email-tpl-nav-${n.key}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors ${active ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}>
                <span className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ background: active ? "rgba(255,255,255,.15)" : n.color + "1a" }}>
                  <Icon className="h-4 w-4" style={{ color: active ? "#fff" : n.color }} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold truncate">{n.label}</span>
                  <span className={`block text-[11.5px] truncate ${active ? "text-slate-300" : "text-slate-400"}`}>{n.sub}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          {isBrand ? (
            <div data-testid="email-tpl-brand-editor">
              <h2 className="text-base font-bold text-slate-800 mb-4">Brand Settings</h2>
              {[["hero_kicker", "Hero kicker"], ["hero_title", "Hero title"], ["powered_by", "Powered-by label"], ["platform_name", "Platform name"], ["company", "Footer company line"], ["generic_accent", "Default accent (hex)"]].map(([k, l]) => (
                <Field key={k} label={l}><Input value={brandForm?.[k] || ""} onChange={(e) => upB(k, e.target.value)} data-testid={`brand-${k}`} /></Field>
              ))}
              {[["hero_tagline", "Hero tagline"], ["trust_text", "Trust bar text"], ["support_line", "Footer support line"], ["team_signoff", "Team sign-off"]].map(([k, l]) => (
                <Field key={k} label={l}><Textarea rows={2} value={brandForm?.[k] || ""} onChange={(e) => upB(k, e.target.value)} data-testid={`brand-${k}`} /></Field>
              ))}
              <Button onClick={saveBrand} disabled={busy} data-testid="brand-save-btn" className="mt-1">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Brand Settings
              </Button>
            </div>
          ) : isGeneric ? (
            <div data-testid="email-tpl-generic-info">
              <h2 className="text-base font-bold text-slate-800 mb-2">Standard Notification</h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">This is the shared branded layout automatically applied to all non-welcome emails — password resets, magic links, invites, quotes and admin alerts. Its look is controlled by <b>Brand Settings</b>. Use the preview and test-send on the right to verify.</p>
            </div>
          ) : form ? (
            <div data-testid="email-tpl-editor">
              <h2 className="text-base font-bold text-slate-800 mb-4">{data.templates.find((t) => t.key === activeKey)?.label}</h2>
              <Field label="Subject line"><Input value={form.subject || ""} onChange={(e) => up("subject", e.target.value)} data-testid="tpl-subject" /></Field>
              <Field label="Greeting intro"><Textarea rows={3} value={form.greeting_intro || ""} onChange={(e) => up("greeting_intro", e.target.value)} data-testid="tpl-greeting" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Role label"><Input value={form.role_label || ""} onChange={(e) => up("role_label", e.target.value)} data-testid="tpl-role-label" /></Field>
                <Field label="Accent color (hex)"><Input value={form.accent || ""} onChange={(e) => up("accent", e.target.value)} data-testid="tpl-accent" /></Field>
              </div>
              <Field label="Role description"><Textarea rows={2} value={form.role_desc || ""} onChange={(e) => up("role_desc", e.target.value)} data-testid="tpl-role-desc" /></Field>
              <Field label="What You Can Do (one per line)"><Textarea rows={5} value={form.bullets || ""} onChange={(e) => up("bullets", e.target.value)} data-testid="tpl-bullets" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Next-step title"><Input value={form.next_step_title || ""} onChange={(e) => up("next_step_title", e.target.value)} data-testid="tpl-next-title" /></Field>
                <Field label="Next-step body"><Input value={form.next_step_body || ""} onChange={(e) => up("next_step_body", e.target.value)} data-testid="tpl-next-body" /></Field>
              </div>
              <Button onClick={saveTemplate} disabled={busy} data-testid="tpl-save-btn" className="mt-1">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Template
              </Button>
            </div>
          ) : null}

          {/* Test send */}
          <div className="mt-6 pt-5 border-t border-slate-100">
            <Label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Send a test email</Label>
            <div className="flex gap-2">
              <Input placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="flex-1" data-testid="tpl-test-email" />
              <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="tpl-test-send-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send Test
              </Button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Mobile"], ["text", FileText, "Plain text"]].map(([m, Icon, l]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`preview-mode-${m}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                <Icon className="h-3.5 w-3.5" />{l}
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-100 p-3 overflow-auto" style={{ height: "72vh" }} data-testid="email-preview-container">
            {mode === "text" ? (
              <pre className="bg-white rounded-lg p-4 text-[12px] text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{preview.text || "…"}</pre>
            ) : (
              <div className="mx-auto bg-white rounded-lg shadow-sm overflow-hidden transition-all" style={{ width: mode === "mobile" ? 380 : "100%" }}>
                <iframe title="email-preview" srcDoc={preview.html} className="w-full border-0" style={{ height: "68vh" }} data-testid="email-preview-iframe" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesAdminPage;
